import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import { signOut } from '../auth/signOut'
import { api, CONSENT_REQUIRED_EVENT, friendlyError, type ConsentStatus } from '../lib/api'
import { hasDriverSignIn } from '../lib/driverInviteSignIn'
import { hasPublicTrackSaveIntentForLocation } from '../lib/publicTrackSaveIntent'
import { cabinetOfPath } from './layout/navigation'
import LegalConsentChecklist, { allConsentsGiven, NO_CONSENTS, type LegalConsents } from './LegalConsentChecklist'

type CabinetRole = 'driver' | 'manager'

// The same two answers, in the same words, as the first step of our own registration form.
const ROLES: { value: CabinetRole; label: string; sub: string }[] = [
  { value: 'driver', label: 'Водитель', sub: 'Выполняю доставки' },
  { value: 'manager', label: 'Менеджер', sub: 'Управляю логистикой' },
]

// Kept in AuthProvider under this key. Only a browser that already finished this flow once has it.
const ACTIVE_ROLE_KEY = 'lupa_active_role'

function hasStoredCabinet(): boolean {
  try {
    return localStorage.getItem(ACTIVE_ROLE_KEY) !== null
  } catch {
    return false
  }
}

// Paths that must stay readable while the gate is up: the consent texts themselves (the user
// has to be able to read what they are accepting) and ordinary public tracking views, which are
// not the account holder's data at all. A public page with a matching pending save is different:
// it is about to write account data, so the existing gate must be allowed to continue that flow.
const EXEMPT_PREFIXES = ['/legal', '/track/']

// Shown when the backend announced a 403 but the status lookup itself did not come back, so we
// know the gate is up without knowing which kind it is. The first-time wording is the safe
// default: it explains the requirement from scratch rather than referring to an earlier consent
// the person may never have given.
const UNKNOWN_REQUIRED: ConsentStatus = {
  required: true,
  reconsent: false,
  consent_version: '',
  terms_version: '',
}

/**
 * 152-ФЗ consent gate.
 *
 * Two different situations land here. Registration collects the personal-data consent in
 * Register.tsx, before the account exists; social login (Яндекс / VK ID) has no such step, so
 * Keycloak creates the account and the user reaches the app never having been asked. And an
 * account that did consent can still be sent back if the substance of the consent document
 * changed afterwards — a consent under ст. 9 ч. 4 covers a specific list of purposes, data and
 * recipients, so a new purpose is not covered by the old acceptance. The backend refuses to
 * serve either case (403 CONSENT_REQUIRED) until a current consent is recorded, and this screen
 * is where that happens.
 *
 * A plain version bump does NOT bring this up — only the backend raising its re-acceptance
 * threshold by hand does. Editorial changes to the texts reach users through the non-blocking
 * LegalUpdateNotice instead.
 *
 * It renders as an overlay rather than replacing the page, so the документы it links to stay
 * reachable in a second tab.
 *
 * A first-time social login is also the only chance to ask who the person is: Яндекс and VK ID
 * hand every account the manager role (a hardcoded-role mapper in Keycloak), so without the
 * question a driver would land in the manager cabinet. «Водитель» adds the driver role through
 * POST /api/profile/roles, which also records it on the account. A re-acceptance already has a
 * role and a cabinet, so the question is not shown. The app asks the same on its consent screen
 * (mobile/src/screens/auth/ConsentScreen.tsx).
 */
export default function ConsentGate() {
  const { authenticated, setActiveRole } = useAuth()
  const location = useLocation()
  const [status, setStatus] = useState<ConsentStatus | null>(null)
  // Pressing «Войти как водитель» on an invite already answered the question; it stays changeable.
  const [role, setRole] = useState<CabinetRole | null>(() => (hasDriverSignIn() ? 'driver' : null))
  const [consents, setConsents] = useState<LegalConsents>(NO_CONSENTS)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!authenticated) {
      setStatus(null)
      return
    }

    let alive = true
    // A network failure here must not lock the user out of the app: the backend gate is the
    // real enforcement, and it announces itself through CONSENT_REQUIRED_EVENT anyway.
    const load = (fallback: ConsentStatus | null) =>
      api
        .consentStatus()
        .then((s) => {
          if (alive) setStatus(s)
        })
        .catch(() => {
          if (alive && fallback) setStatus(fallback)
        })

    load(null)

    // The 403 tells us the gate is up but not which wording to use, so re-read the status;
    // if that read fails too we still raise the gate, just with the generic text. A browser that
    // remembers a cabinet has been through this before, which makes it a re-acceptance: asking
    // the role again could move a driver into the manager cabinet.
    const onRequired = () => {
      void load({ ...UNKNOWN_REQUIRED, reconsent: hasStoredCabinet() })
    }
    window.addEventListener(CONSENT_REQUIRED_EVENT, onRequired)
    return () => {
      alive = false
      window.removeEventListener(CONSENT_REQUIRED_EVENT, onRequired)
    }
  }, [authenticated])

  const askRole = Boolean(status?.required && !status.reconsent)

  const submit = useCallback(async () => {
    setSubmitting(true)
    setError('')
    try {
      // Order matters: the account is provisioned only once the consent is on record, and the
      // role grant needs it. Accepting twice is harmless, so a failure below is safe to retry.
      await api.acceptConsent()
      if (askRole && role) {
        if (role === 'driver') await api.grantRole('driver')
        setActiveRole(role)
        // The page decides the cabinet (cabinetOfPath), so reloading /manager would put a driver
        // straight back into the manager cabinet. Pages both share reload in place — an invite or
        // a pending save continues where it was.
        const cabinet = cabinetOfPath(location.pathname)
        if (cabinet && cabinet !== role) {
          window.location.assign(`/${role}`)
          return
        }
      }
      // Reload rather than just hiding the overlay: the pages underneath were rendered while
      // every API call was being refused, so their data is missing, not stale. The reload also
      // fetches a fresh token, which is what carries a just-granted role.
      window.location.reload()
    } catch (e) {
      setError(friendlyError(e))
      setSubmitting(false)
    }
  }, [askRole, role, setActiveRole, location.pathname])

  if (!status?.required) return null
  const hasPendingPublicSave = hasPublicTrackSaveIntentForLocation(location.pathname, location.search)
  if (EXEMPT_PREFIXES.some((p) => location.pathname.startsWith(p)) && !hasPendingPublicSave) return null

  return (
    <>
      <style>{`
        .consent-gate {
          position: fixed; inset: 0; z-index: 2000;
          background: rgba(15, 23, 42, 0.72);
          display: flex; align-items: center; justify-content: center;
          padding: 16px;
          font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, sans-serif;
        }
        .consent-gate__card {
          width: 100%; max-width: 480px; max-height: 100%; overflow-y: auto;
          background: #fff; border-radius: 18px; padding: 26px 24px;
          box-shadow: 0 24px 60px rgba(0,0,0,0.28);
        }
        .consent-gate__title { margin: 0 0 10px; font-size: 19px; font-weight: 700; color: #0f172a; }
        .consent-gate__lead { margin: 0 0 18px; font-size: 13.5px; line-height: 1.6; color: #475569; }
        .consent-gate__btn {
          width: 100%; padding: 12px 18px; border: none; border-radius: 12px;
          background: #15803d; color: #fff; font-size: 14px; font-weight: 600;
          cursor: pointer; font-family: inherit;
        }
        .consent-gate__btn:disabled { background: #cbd5e1; cursor: not-allowed; }
        .consent-gate__logout {
          display: block; margin: 14px auto 0; background: none; border: none;
          color: #64748b; font-size: 12.5px; cursor: pointer; font-family: inherit;
          text-decoration: underline; text-underline-offset: 2px;
        }
        .consent-gate__roles { margin: 0 0 18px; padding: 0; border: none; }
        .consent-gate__roles-title { margin: 0 0 8px; padding: 0; font-size: 13.5px; font-weight: 600; color: #0f172a; }
        .consent-gate__role-list { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
        .consent-gate__role {
          display: flex; flex-direction: column; gap: 2px; text-align: left;
          padding: 11px 12px; border-radius: 12px; border: 1.5px solid #e2e8f0;
          background: #fff; cursor: pointer; font-family: inherit;
        }
        .consent-gate__role[aria-checked="true"] { border-color: #15803d; background: #f0fdf4; }
        .consent-gate__role-label { font-size: 14px; font-weight: 600; color: #0f172a; }
        .consent-gate__role-sub { font-size: 12px; color: #64748b; }
        .consent-gate__error {
          margin: 0 0 12px; padding: 9px 12px; border-radius: 10px;
          background: #fef2f2; color: #b91c1c; font-size: 12.5px; line-height: 1.5;
        }
      `}</style>
      <div className="consent-gate" role="dialog" aria-modal="true" aria-labelledby="consent-gate-title">
        <div className="consent-gate__card">
          <h2 className="consent-gate__title" id="consent-gate-title">
            {status.reconsent ? 'Согласие нужно подтвердить заново' : 'Ещё один шаг'}
          </h2>
          <p className="consent-gate__lead">
            {status.reconsent ? (
              <>
                Мы изменили состав данных, которые обрабатываем, поэтому ваше прежнее согласие
                {status.accepted_consent_version ? ` (редакция ${status.accepted_consent_version})` : ''}{' '}
                больше не охватывает работу сервиса. Посмотрите новую редакцию и подтвердите
                согласие — без него мы не вправе продолжать обработку.
              </>
            ) : (
              <>
                Вы вошли через сторонний сервис, поэтому мы не успели спросить главное: без вашего
                согласия мы не вправе обрабатывать персональные данные и не создаём аккаунт в LUPA.
              </>
            )}
          </p>

          {error && <p className="consent-gate__error">{error}</p>}

          {askRole && (
            <div className="consent-gate__roles" role="radiogroup" aria-labelledby="consent-gate-roles-title">
              <p className="consent-gate__roles-title" id="consent-gate-roles-title">Кем вы работаете?</p>
              <div className="consent-gate__role-list">
                {ROLES.map((r) => (
                  <button
                    key={r.value}
                    type="button"
                    role="radio"
                    aria-checked={role === r.value}
                    className="consent-gate__role"
                    onClick={() => setRole(r.value)}
                  >
                    <span className="consent-gate__role-label">{r.label}</span>
                    <span className="consent-gate__role-sub">{r.sub}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <LegalConsentChecklist value={consents} onChange={setConsents} />

          <button
            type="button"
            className="consent-gate__btn"
            disabled={!allConsentsGiven(consents) || (askRole && !role) || submitting}
            onClick={submit}
          >
            {submitting ? 'Сохраняем...' : 'Принять и продолжить'}
          </button>

          <button
            type="button"
            className="consent-gate__logout"
            onClick={() => { void signOut() }}
          >
            Выйти
          </button>
        </div>
      </div>
    </>
  )
}
